
import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { v4 as uuidv4 } from "uuid";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { 
  ProjetoSolar,
  concessionarias,
  regionaisEcoefin,
} from "@/types";
import { useToast } from "@/hooks/use-toast";
import { FileUp, FilePlus2, Trash2 } from "lucide-react";

const formSchema = z.object({
  // Dados do Integrador
  regional: z.string().optional(),
  
  // Dados da Unidade Consumidora
  concessionaria: z.string().optional(),
  titular: z.string().optional(),
  cpfCnpj: z.string().optional(),
  rg: z.string().optional(),
  email: z.string().email().optional().or(z.literal("")),
  celular: z.string().optional(),
  numeroInstalacao: z.string().optional(),
  numeroCliente: z.string().optional(),
  endereco: z.string().optional(),
  cep: z.string().optional(),
  coordenadas: z.string().optional(),
  
  // Dados Elétricos
  classe: z.string().optional(),
  tipoTensao: z.string().optional(),
  tipoPadrao: z.string().optional(),
  nivelTensao: z.string().optional(),
  ramal: z.string().optional(),
  disjuntor: z.string().optional(),
  aumentoCarga: z.string().optional(),
  novoPadrao: z.string().optional(),
  novoDisjuntor: z.string().optional(),
  potenciaTrafo: z.string().optional(),
  
  // Dados Mecânicos
  telhado: z.string().optional(),
  estrutura: z.string().optional(),
  direcaoInclinacao: z.string().optional(),
  
  // Dados Financeiros
  formaPagamento: z.string().optional(),
  valorVenda: z.string().optional(),
  numeroProposta: z.string().optional(),
  observacaoFinanceiro: z.string().optional(),
  
  // Dados para Compensar
  uc1: z.string().optional(),
  enderecoUc1: z.string().optional(),
  porcentagem1: z.string().optional(),
  uc2: z.string().optional(),
  enderecoUc2: z.string().optional(),
  porcentagem2: z.string().optional(),
  uc3: z.string().optional(),
  enderecoUc3: z.string().optional(),
  porcentagem3: z.string().optional(),
  observacaoCompensacao: z.string().optional(),
});

type FormValues = z.infer<typeof formSchema>;

interface ProjetoChecklistFormProps {
  projeto?: ProjetoSolar;
  onSave: (data: any) => void;
  onCancel: () => void;
}

const ProjetoChecklistForm = ({ projeto, onSave, onCancel }: ProjetoChecklistFormProps) => {
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState("integrador");
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [documentos, setDocumentos] = useState<{ id: string; nome: string; arquivo: File }[]>([]);
  
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      // Dados do Integrador
      regional: projeto?.dadosIntegrador?.regional || "",
      
      // Dados da Unidade Consumidora
      concessionaria: projeto?.dadosUnidadeConsumidora?.concessionaria || "",
      titular: projeto?.dadosUnidadeConsumidora?.titular || "",
      cpfCnpj: projeto?.dadosUnidadeConsumidora?.cpfCnpj || "",
      rg: projeto?.dadosUnidadeConsumidora?.rg || "",
      email: projeto?.dadosUnidadeConsumidora?.email || "",
      celular: projeto?.dadosUnidadeConsumidora?.celular || "",
      numeroInstalacao: projeto?.dadosUnidadeConsumidora?.numeroInstalacao || "",
      numeroCliente: projeto?.dadosUnidadeConsumidora?.numeroCliente || "",
      endereco: projeto?.dadosUnidadeConsumidora?.endereco || "",
      cep: projeto?.dadosUnidadeConsumidora?.cep || "",
      coordenadas: projeto?.dadosUnidadeConsumidora?.coordenadas || "",
      
      // Dados Elétricos
      classe: projeto?.dadosEletricos?.classe || "",
      tipoTensao: projeto?.dadosEletricos?.tipoTensao || "",
      tipoPadrao: projeto?.dadosEletricos?.tipoPadrao || "",
      nivelTensao: projeto?.dadosEletricos?.nivelTensao || "",
      ramal: projeto?.dadosEletricos?.ramal || "",
      disjuntor: projeto?.dadosEletricos?.disjuntor || "",
      aumentoCarga: projeto?.dadosEletricos?.aumentoCarga || "",
      novoPadrao: projeto?.dadosEletricos?.novoPadrao || "",
      novoDisjuntor: projeto?.dadosEletricos?.novoDisjuntor || "",
      potenciaTrafo: projeto?.dadosEletricos?.potenciaTrafo || "",
      
      // Dados Mecânicos
      telhado: projeto?.dadosMecanicos?.telhado || "",
      estrutura: projeto?.dadosMecanicos?.estrutura || "",
      direcaoInclinacao: projeto?.dadosMecanicos?.direcaoInclinacao || "",
      
      // Dados Financeiros
      formaPagamento: projeto?.dadosFinanceiros?.formaPagamento || "",
      valorVenda: projeto?.dadosFinanceiros?.valorVenda?.toString() || "",
      numeroProposta: projeto?.dadosFinanceiros?.numeroProposta || "",
      observacaoFinanceiro: projeto?.dadosFinanceiros?.observacao || "",
      
      // Dados para Compensar
      uc1: projeto?.dadosCompensacao?.uc1 || "",
      enderecoUc1: projeto?.dadosCompensacao?.enderecoUc1 || "",
      porcentagem1: projeto?.dadosCompensacao?.porcentagem1 || "",
      uc2: projeto?.dadosCompensacao?.uc2 || "",
      enderecoUc2: projeto?.dadosCompensacao?.enderecoUc2 || "",
      porcentagem2: projeto?.dadosCompensacao?.porcentagem2 || "",
      uc3: projeto?.dadosCompensacao?.uc3 || "",
      enderecoUc3: projeto?.dadosCompensacao?.enderecoUc3 || "",
      porcentagem3: projeto?.dadosCompensacao?.porcentagem3 || "",
      observacaoCompensacao: projeto?.dadosCompensacao?.observacao || "",
    },
  });
  
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const newFiles = Array.from(e.target.files);
      setUploadedFiles((prevFiles) => [...prevFiles, ...newFiles]);
      
      // Create document entries
      const newDocuments = newFiles.map(file => ({
        id: uuidv4(),
        nome: file.name,
        arquivo: file
      }));
      
      setDocumentos(prev => [...prev, ...newDocuments]);
    }
  };
  
  const removeDocument = (id: string) => {
    setDocumentos(prev => prev.filter(doc => doc.id !== id));
  };
  
  const onSubmit = (values: FormValues) => {
    try {
      // Transform form values to project structure
      const projetoData = {
        dadosIntegrador: {
          regional: values.regional,
        },
        dadosUnidadeConsumidora: {
          concessionaria: values.concessionaria,
          titular: values.titular,
          cpfCnpj: values.cpfCnpj,
          rg: values.rg,
          email: values.email,
          celular: values.celular,
          numeroInstalacao: values.numeroInstalacao,
          numeroCliente: values.numeroCliente,
          endereco: values.endereco,
          cep: values.cep,
          coordenadas: values.coordenadas,
        },
        dadosEletricos: {
          classe: values.classe as any,
          tipoTensao: values.tipoTensao as any,
          tipoPadrao: values.tipoPadrao as any,
          nivelTensao: values.nivelTensao as any,
          ramal: values.ramal as any,
          disjuntor: values.disjuntor,
          aumentoCarga: values.aumentoCarga as any,
          novoPadrao: values.novoPadrao,
          novoDisjuntor: values.novoDisjuntor,
          potenciaTrafo: values.potenciaTrafo,
        },
        dadosMecanicos: {
          telhado: values.telhado as any,
          estrutura: values.estrutura as any,
          direcaoInclinacao: values.direcaoInclinacao as any,
        },
        dadosFinanceiros: {
          formaPagamento: values.formaPagamento as any,
          valorVenda: values.valorVenda ? parseFloat(values.valorVenda) : undefined,
          numeroProposta: values.numeroProposta,
          observacao: values.observacaoFinanceiro,
        },
        dadosCompensacao: {
          uc1: values.uc1,
          enderecoUc1: values.enderecoUc1,
          porcentagem1: values.porcentagem1,
          uc2: values.uc2,
          enderecoUc2: values.enderecoUc2,
          porcentagem2: values.porcentagem2,
          uc3: values.uc3,
          enderecoUc3: values.enderecoUc3,
          porcentagem3: values.porcentagem3,
          observacao: values.observacaoCompensacao,
        },
        documentos: documentos.map(doc => ({
          id: doc.id,
          nome: doc.nome,
          tipo: doc.nome.split('.').pop() || 'unknown',
          url: URL.createObjectURL(doc.arquivo),
          dataUpload: new Date(),
          tamanho: doc.arquivo.size,
          uploadedBy: "current-user-id" // In a real app, this would be the current user's ID
        })),
      };
      
      onSave(projetoData);
      
      toast({
        title: "Dados salvos",
        description: "As informações do projeto foram salvas com sucesso.",
      });
    } catch (error) {
      console.error("Erro ao salvar dados do projeto:", error);
      toast({
        title: "Erro ao salvar",
        description: "Ocorreu um erro ao salvar os dados do projeto.",
        variant: "destructive",
      });
    }
  };
  
  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid grid-cols-6 mb-4">
            <TabsTrigger value="integrador">Integrador</TabsTrigger>
            <TabsTrigger value="unidade">Unid. Consumidora</TabsTrigger>
            <TabsTrigger value="eletricos">Dados Elétricos</TabsTrigger>
            <TabsTrigger value="mecanicos">Dados Mecânicos</TabsTrigger>
            <TabsTrigger value="financeiros">Dados Financeiros</TabsTrigger>
            <TabsTrigger value="documentos">Documentos</TabsTrigger>
          </TabsList>
          
          <TabsContent value="integrador">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  📌 Dados do Integrador
                </CardTitle>
                <CardDescription>
                  Informações sobre o integrador responsável pelo projeto
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <FormField
                  control={form.control}
                  name="regional"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Regional:</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Selecione a regional" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {regionaisEcoefin.map((regional) => (
                            <SelectItem key={regional} value={regional}>
                              {regional}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </CardContent>
              <CardFooter className="flex justify-between">
                <Button variant="outline" type="button" onClick={onCancel}>
                  Cancelar
                </Button>
                <Button 
                  type="button" 
                  onClick={() => setActiveTab("unidade")}
                >
                  Próximo
                </Button>
              </CardFooter>
            </Card>
          </TabsContent>
          
          <TabsContent value="unidade">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  🏠 Dados da Unidade Consumidora
                </CardTitle>
                <CardDescription>
                  Informações sobre o cliente e local da instalação
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="concessionaria"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Concessionária:</FormLabel>
                        <Select onValueChange={field.onChange} defaultValue={field.value}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Selecione a concessionária" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {concessionarias.map((concessionaria) => (
                              <SelectItem key={concessionaria} value={concessionaria}>
                                {concessionaria}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <FormField
                    control={form.control}
                    name="titular"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Titular (UC):</FormLabel>
                        <FormControl>
                          <Input placeholder="Nome completo do cliente" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <FormField
                    control={form.control}
                    name="cpfCnpj"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>CPF/CNPJ:</FormLabel>
                        <FormControl>
                          <Input placeholder="00000000000" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <FormField
                    control={form.control}
                    name="rg"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>RG:</FormLabel>
                        <FormControl>
                          <Input placeholder="Número do documento" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <FormField
                    control={form.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>E-mail:</FormLabel>
                        <FormControl>
                          <Input type="email" placeholder="exemplo@dominio.com" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <FormField
                    control={form.control}
                    name="celular"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Celular:</FormLabel>
                        <FormControl>
                          <Input placeholder="00000000000" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <FormField
                    control={form.control}
                    name="numeroInstalacao"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Número de Instalação:</FormLabel>
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <FormField
                    control={form.control}
                    name="numeroCliente"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Número do Cliente:</FormLabel>
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <FormField
                    control={form.control}
                    name="endereco"
                    render={({ field }) => (
                      <FormItem className="col-span-2">
                        <FormLabel>Endereço:</FormLabel>
                        <FormControl>
                          <Input placeholder="Endereço completo" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <FormField
                    control={form.control}
                    name="cep"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>CEP:</FormLabel>
                        <FormControl>
                          <Input placeholder="00000000" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <FormField
                    control={form.control}
                    name="coordenadas"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Coordenadas:</FormLabel>
                        <FormControl>
                          <Input 
                            placeholder="https://maps/search/casa/@-19.9767603,-44.0215606,17" 
                            {...field} 
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </CardContent>
              <CardFooter className="flex justify-between">
                <Button 
                  variant="outline" 
                  type="button" 
                  onClick={() => setActiveTab("integrador")}
                >
                  Anterior
                </Button>
                <Button 
                  type="button" 
                  onClick={() => setActiveTab("eletricos")}
                >
                  Próximo
                </Button>
              </CardFooter>
            </Card>
          </TabsContent>
          
          <TabsContent value="eletricos">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  ⚡ Dados Elétricos
                </CardTitle>
                <CardDescription>
                  Informações sobre a instalação elétrica
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <FormField
                    control={form.control}
                    name="classe"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Classe:</FormLabel>
                        <Select onValueChange={field.onChange} defaultValue={field.value}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Selecione a classe" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="Residencial">Residencial</SelectItem>
                            <SelectItem value="Comercial">Comercial</SelectItem>
                            <SelectItem value="Rural">Rural</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <FormField
                    control={form.control}
                    name="tipoTensao"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Tipo de Tensão:</FormLabel>
                        <Select onValueChange={field.onChange} defaultValue={field.value}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Selecione o tipo" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="Baixa">Baixa</SelectItem>
                            <SelectItem value="Média">Média</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <FormField
                    control={form.control}
                    name="tipoPadrao"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Tipo de Padrão:</FormLabel>
                        <Select onValueChange={field.onChange} defaultValue={field.value}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Selecione o padrão" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="Monofásico">Monofásico</SelectItem>
                            <SelectItem value="Bifásico">Bifásico</SelectItem>
                            <SelectItem value="Trifásico">Trifásico</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <FormField
                    control={form.control}
                    name="nivelTensao"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Nível de Tensão:</FormLabel>
                        <Select onValueChange={field.onChange} defaultValue={field.value}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Selecione o nível" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="127/220">127/220</SelectItem>
                            <SelectItem value="220/380">220/380</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <FormField
                    control={form.control}
                    name="ramal"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Ramal:</FormLabel>
                        <Select onValueChange={field.onChange} defaultValue={field.value}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Selecione o ramal" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="Aéreo">Aéreo</SelectItem>
                            <SelectItem value="Subterrâneo">Subterrâneo</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <FormField
                    control={form.control}
                    name="disjuntor"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Disjuntor:</FormLabel>
                        <Select onValueChange={field.onChange} defaultValue={field.value}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Selecione o disjuntor" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {["32", "40", "50", "60", "63", "70", "80", "90", "100", "125", "150", "200", "225", "250", "Outro"].map(
                              (value) => (
                                <SelectItem key={value} value={value}>
                                  {value}
                                </SelectItem>
                              )
                            )}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <FormField
                    control={form.control}
                    name="aumentoCarga"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Aumento de Carga?:</FormLabel>
                        <Select onValueChange={field.onChange} defaultValue={field.value}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Selecione" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="Não">Não</SelectItem>
                            <SelectItem value="Sim">Sim</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <FormField
                    control={form.control}
                    name="novoPadrao"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Novo Medidor:</FormLabel>
                        <Select onValueChange={field.onChange} defaultValue={field.value}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Selecione o medidor" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="------">------</SelectItem>
                            <SelectItem value="Monofásico">Monofásico</SelectItem>
                            <SelectItem value="Bifásico">Bifásico</SelectItem>
                            <SelectItem value="Trifásico">Trifásico</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <FormField
                    control={form.control}
                    name="novoDisjuntor"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Novo Disjuntor:</FormLabel>
                        <Select onValueChange={field.onChange} defaultValue={field.value}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Selecione o disjuntor" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="--">--</SelectItem>
                            {["32", "40", "50", "60", "63", "70", "80", "90", "100", "125", "150", "200", "225", "250", "Outro"].map(
                              (value) => (
                                <SelectItem key={value} value={value}>
                                  {value}
                                </SelectItem>
                              )
                            )}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <FormField
                    control={form.control}
                    name="potenciaTrafo"
                    render={({ field }) => (
                      <FormItem className="col-span-2">
                        <FormLabel>Potência do Trafo:</FormLabel>
                        <Select onValueChange={field.onChange} defaultValue={field.value}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Selecione a potência" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="--">--</SelectItem>
                            {["10", "15", "25", "35", "45", "50", "60", "75", "112,5", "Outro"].map(
                              (value) => (
                                <SelectItem key={value} value={value}>
                                  {value}
                                </SelectItem>
                              )
                            )}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </CardContent>
              <CardFooter className="flex justify-between">
                <Button 
                  variant="outline" 
                  type="button" 
                  onClick={() => setActiveTab("unidade")}
                >
                  Anterior
                </Button>
                <Button 
                  type="button" 
                  onClick={() => setActiveTab("mecanicos")}
                >
                  Próximo
                </Button>
              </CardFooter>
            </Card>
          </TabsContent>
          
          <TabsContent value="mecanicos">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  🔧 Dados Mecânicos
                </CardTitle>
                <CardDescription>
                  Informações sobre estrutura e instalação
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <FormField
                    control={form.control}
                    name="telhado"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Telhado:</FormLabel>
                        <Select onValueChange={field.onChange} defaultValue={field.value}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Selecione o tipo" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {["Metálico", "Cerâmica", "Fibromadeira", "Fibrocimento", "Solo", "Laje", "Carport"].map(
                              (value) => (
                                <SelectItem key={value} value={value}>
                                  {value}
                                </SelectItem>
                              )
                            )}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <FormField
                    control={form.control}
                    name="estrutura"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Estrutura:</FormLabel>
                        <Select onValueChange={field.onChange} defaultValue={field.value}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Selecione o tipo" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="Metálico">Metálico</SelectItem>
                            <SelectItem value="Madeira">Madeira</SelectItem>
                            <SelectItem value="Cimentício">Cimentício</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <FormField
                    control={form.control}
                    name="direcaoInclinacao"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Direção/Telhado:</FormLabel>
                        <Select onValueChange={field.onChange} defaultValue={field.value}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Selecione a direção" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="Norte">Norte</SelectItem>
                            <SelectItem value="Sul">Sul</SelectItem>
                            <SelectItem value="Leste">Leste</SelectItem>
                            <SelectItem value="Oeste">Oeste</SelectItem>
                            <SelectItem value="Leste_Oeste">Leste-Oeste</SelectItem>
                            <SelectItem value="Norte_Sul">Norte-Sul</SelectItem>
                            <SelectItem value="Outra">Outra</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </CardContent>
              <CardFooter className="flex justify-between">
                <Button 
                  variant="outline" 
                  type="button" 
                  onClick={() => setActiveTab("eletricos")}
                >
                  Anterior
                </Button>
                <Button 
                  type="button" 
                  onClick={() => setActiveTab("financeiros")}
                >
                  Próximo
                </Button>
              </CardFooter>
            </Card>
          </TabsContent>
          
          <TabsContent value="financeiros">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  💲 Dados Financeiros e de Compensação
                </CardTitle>
                <CardDescription>
                  Informações sobre pagamento e unidades para compensação
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 gap-8">
                  <div>
                    <h3 className="text-lg font-medium mb-4">Dados Financeiros</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <FormField
                        control={form.control}
                        name="formaPagamento"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Forma de Pagamento:</FormLabel>
                            <Select onValueChange={field.onChange} defaultValue={field.value}>
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue placeholder="Selecione a forma" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                <SelectItem value="Boleto">Boleto</SelectItem>
                                <SelectItem value="Cartão">Cartão</SelectItem>
                                <SelectItem value="Financiamento">Financiamento</SelectItem>
                                <SelectItem value="Misto">Misto</SelectItem>
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      
                      <FormField
                        control={form.control}
                        name="valorVenda"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Valor da Venda:</FormLabel>
                            <FormControl>
                              <Input placeholder="Valor da Venda" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      
                      <FormField
                        control={form.control}
                        name="numeroProposta"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Número da Proposta:</FormLabel>
                            <FormControl>
                              <Input placeholder="Número da Proposta" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      
                      <FormField
                        control={form.control}
                        name="observacaoFinanceiro"
                        render={({ field }) => (
                          <FormItem className="col-span-3">
                            <FormLabel>Observação:</FormLabel>
                            <FormControl>
                              <Textarea 
                                placeholder="Descreva algo que precise" 
                                className="min-h-20"
                                {...field} 
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  </div>
                  
                  <div>
                    <h3 className="text-lg font-medium mb-4">Sistema de Compensação</h3>
                    <div className="space-y-6">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <FormField
                          control={form.control}
                          name="uc1"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Num. UC 1:</FormLabel>
                              <FormControl>
                                <Input placeholder="Unidade Consumidora 01" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        
                        <FormField
                          control={form.control}
                          name="enderecoUc1"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Endereço UC 1:</FormLabel>
                              <FormControl>
                                <Input placeholder="Endereço da UC 01" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        
                        <FormField
                          control={form.control}
                          name="porcentagem1"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Porcentagem:</FormLabel>
                              <FormControl>
                                <Input placeholder="Qual Percentual %" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <FormField
                          control={form.control}
                          name="uc2"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Num. UC 2:</FormLabel>
                              <FormControl>
                                <Input placeholder="Unidade Consumidora 02" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        
                        <FormField
                          control={form.control}
                          name="enderecoUc2"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Endereço UC 2:</FormLabel>
                              <FormControl>
                                <Input placeholder="Endereço da UC 02" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        
                        <FormField
                          control={form.control}
                          name="porcentagem2"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Porcentagem:</FormLabel>
                              <FormControl>
                                <Input placeholder="Qual Percentual %" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <FormField
                          control={form.control}
                          name="uc3"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Num. UC 3:</FormLabel>
                              <FormControl>
                                <Input placeholder="Unidade Consumidora 03" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        
                        <FormField
                          control={form.control}
                          name="enderecoUc3"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Endereço UC 3:</FormLabel>
                              <FormControl>
                                <Input placeholder="Endereço da UC 03" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        
                        <FormField
                          control={form.control}
                          name="porcentagem3"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Porcentagem:</FormLabel>
                              <FormControl>
                                <Input placeholder="Qual Percentual %" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                      
                      <FormField
                        control={form.control}
                        name="observacaoCompensacao"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Observação:</FormLabel>
                            <FormControl>
                              <Textarea 
                                placeholder="Descreva algo que precise" 
                                className="min-h-20"
                                {...field} 
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  </div>
                </div>
              </CardContent>
              <CardFooter className="flex justify-between">
                <Button 
                  variant="outline" 
                  type="button" 
                  onClick={() => setActiveTab("mecanicos")}
                >
                  Anterior
                </Button>
                <Button 
                  type="button" 
                  onClick={() => setActiveTab("documentos")}
                >
                  Próximo
                </Button>
              </CardFooter>
            </Card>
          </TabsContent>
          
          <TabsContent value="documentos">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  📎 Documentos Anexos
                </CardTitle>
                <CardDescription>
                  Anexe os documentos necessários para o projeto
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  <div className="flex flex-col gap-2">
                    <div className="flex items-center gap-2">
                      <Button 
                        type="button" 
                        onClick={() => document.getElementById('file-upload')?.click()}
                        variant="outline"
                        className="w-full flex items-center justify-center gap-2"
                      >
                        <FileUp className="h-4 w-4" />
                        Selecionar Arquivos
                      </Button>
                      <input
                        id="file-upload"
                        type="file"
                        multiple
                        className="hidden"
                        onChange={handleFileChange}
                      />
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Você pode anexar até 15 arquivos (PDFs, imagens, documentos)
                    </p>
                  </div>
                  
                  {documentos.length > 0 && (
                    <div className="border rounded-md p-4">
                      <h4 className="font-medium mb-2">Documentos anexados</h4>
                      <div className="space-y-2">
                        {documentos.map((doc) => (
                          <div 
                            key={doc.id} 
                            className="flex items-center justify-between p-2 rounded-md bg-muted/50"
                          >
                            <div className="flex items-center gap-2">
                              <FilePlus2 className="h-4 w-4 text-muted-foreground" />
                              <span className="text-sm">{doc.nome}</span>
                            </div>
                            <Button 
                              type="button" 
                              variant="ghost" 
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => removeDocument(doc.id)}
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
              <CardFooter className="flex justify-between">
                <Button 
                  variant="outline" 
                  type="button" 
                  onClick={() => setActiveTab("financeiros")}
                >
                  Anterior
                </Button>
                <Button type="submit">Salvar Projeto</Button>
              </CardFooter>
            </Card>
          </TabsContent>
        </Tabs>
      </form>
    </Form>
  );
};

export default ProjetoChecklistForm;
